import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  githubAuthApiRef,
  gitlabAuthApiRef,
  useApi,
  configApiRef,
} from '@backstage/core-plugin-api';

import { UnifiedThemeProvider } from '@backstage/theme';
import { boxboatThemes } from './themes';

const app = createApp({
  themes: [
    {
      id: 'IBM-light',
      title: 'Light',
      variant: 'light',
      Provider({ children }) {
        return (
          <UnifiedThemeProvider
            theme={boxboatThemes.light}
            children={children}
          />
        );
      },
    },
    {
      id: 'IBM-dark',
      title: 'Dark',
      variant: 'dark',
      Provider({ children }) {
        return (
          <UnifiedThemeProvider
            theme={boxboatThemes.dark}
            children={children}
          />
        );
      },
    },
  ],
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      return (
        <div style={{
          backgroundColor: '#ffffff',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'row',
        }}>
          {/* Left panel - black with IBM logo */}
          <div style={{
            backgroundColor: '#000000',
            width: '40%',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '48px',
          }}>
            <img
              src={require('./themes/02_8-bar-reverse.svg')}
              alt="IBM"
              style={{ width: 200, height: 'auto' }}
            />
            <div>
              <h1 style={{
                color: '#ffffff',
                fontSize: 54,
                fontWeight: 600,
                fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
                lineHeight: 1.15,
                margin: 0,
                letterSpacing: '-0.02em',
              }}>
                Backstage
              </h1>
              <p style={{
                color: '#c6c6c6',
                fontSize: 16,
                fontWeight: 400,
                fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
                marginTop: 24,
                lineHeight: 1.5,
                letterSpacing: '0.16px',
              }}>
                Internal Developer Portal
              </p>
            </div>
            <p style={{
              color: '#525252',
              fontSize: 12,
              fontFamily: '"IBM Plex Sans", "Helvetica Neue", Arial, sans-serif',
              margin: 0,
            }}>
              © {new Date().getFullYear()} IBM
            </p>
          </div>
          {/* Right panel - white with sign in */}
          <div style={{
            width: '60%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}>
            <style>{`
              .right-signin-panel header { display: none !important; }
              .right-signin-panel article { min-height: 0 !important; height: auto !important; }
              .right-signin-panel > div { min-height: 0 !important; height: auto !important; }
              .right-signin-panel [class*="BackstagePage-root"] { min-height: 0 !important; height: auto !important; }
              .right-signin-panel [class*="BackstageContent-root"] { padding-top: 0 !important; }
              .right-signin-panel [class*="MuiGrid-root"] { justify-content: center !important; }
              .right-signin-panel nav { display: none !important; }
              .right-signin-panel main { min-height: 0 !important; height: auto !important; padding: 0 !important; }
            `}</style>
            <div className="right-signin-panel" style={{ width: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {configApi.getString('auth.environment') === 'development' ? (
                // Local-dev only: app-config.local.yaml sets
                // `auth.environment: development` and enables the guest provider.
                // Production overlays (app-config.github.yaml) set
                // `auth.environment: github`, so this branch is never taken in prod.
                <SignInPage {...props} providers={['guest']} />
              ) : (
                <SignInPage
                  {...props}
                  provider={
                    configApi.getString('auth.environment').includes('github')
                      ? {
                          id: 'github-auth-provider',
                          title: 'GitHub',
                          message: 'Sign in with your GitHub account to continue',
                          apiRef: githubAuthApiRef,
                        }
                      : {
                          id: 'gitlab-auth-provider',
                          title: 'GitLab',
                          message: 'Sign in with your GitLab account to continue',
                          apiRef: gitlabAuthApiRef,
                        }
                  }
                />
              )}
            </div>
          </div>
        </div>
      );
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
